import { useAuth, useLocalStorage } from "@elit-hair/elit-tools";
import { captureMessage } from "@sentry/react";
import { getAnalytics } from "firebase/analytics";
import { initializeApp } from "firebase/app";
import { getMessaging, getToken } from "firebase/messaging";
import { ReactNode, createContext, useContext, useEffect, useMemo } from "react";

import { ENV } from "@/constants/env";
import { LOCAL_STORAGE_KEYS } from "@/constants/local-storage-keys";
import { getVoiceRegistrationToken } from "@/utils/voice-registration.utils";

// Enum for notification sources
export enum NotificationSource {
    FOREGROUND = "foreground",
    SERVICE_WORKER = "serviceWorker",
}

let messaging = null;
try {
    const firebaseConfig = ENV.FIREBASE_CONFIG;

    if (firebaseConfig) {
        const app = initializeApp(firebaseConfig);
        getAnalytics(app);

        // Messaging needs a secure context (HTTPS or localhost); skip on plain http://LAN-IP.
        if (typeof window !== "undefined" && window.isSecureContext) {
            messaging = getMessaging(app);

            if ("serviceWorker" in navigator) {
                try {
                    navigator.serviceWorker
                        .register("/firebase-messaging-sw.js", { scope: "/" })
                        .then((registration) => {
                            console.log("Notifications service Worker registered with scope:", registration.scope);
                        })
                        .catch((error) => {
                            console.error("Service Worker registration failed:", error);
                        });
                } catch (error) {
                    console.error("Service Worker registration failed:", error);
                }
            }
        }
    } else {
        console.error("No firebase config provided");
    }
} catch (error) {
    console.error("Firebase initialization failed:", error);
}

export const createNotificationProvider = <T, Y = unknown>() => {
    type NotificationContextType = T & {
        handleNotification: (notificationData: Y) => void;
    };
    const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

    type NotificationProviderProps = {
        children: ReactNode;
        notificationHandler: (notificationData: Y, context?: { source: NotificationSource }) => void;
        onInit?: (fcmToken: string) => void;
        notificationStates: T;
    };

    const NotificationProvider = ({
        children,
        notificationHandler,
        onInit,
        notificationStates,
    }: NotificationProviderProps) => {
        const { auth } = useAuth();

        const [fcmToken, setFcmToken] = useLocalStorage<string>(
            LOCAL_STORAGE_KEYS.FCM_TOKEN.key,
            LOCAL_STORAGE_KEYS.FCM_TOKEN.default
        );

        useEffect(() => {
            if (!auth || !onInit) return;

            const registrationToken = getVoiceRegistrationToken(fcmToken);
            if (!registrationToken) return;

            void onInit(registrationToken);
        }, [fcmToken, auth, onInit]);

        useEffect(() => {
            if (!messaging || !auth) return;

            if (window?.Notification?.permission !== "granted") {
                console.log("No permission to access notifications");
                return;
            }

            const applyToken = async () => {
                const token = await getToken(messaging, {
                    vapidKey: ENV.FIREBASE_VAPID_KEY,
                });
                if (token) {
                    setFcmToken(token);
                } else {
                    console.log("No registration token available.");
                }
            };

            applyToken();

            // DEPRECATED: This is no longer used since we are using the service worker to handle notifications.
            // This way we avoid duplicating the notification handler for foreground and background.
            // Set up message handler for foreground messages (app is open and visible)
            // const unsubscribe = onMessage(messaging, (payload) => {
            //     notificationHandler(payload as unknown as Y, { source: NotificationSource.FOREGROUND });
            // });

            // Set up listener for messages from service worker (app is in background)
            const handleServiceWorkerMessageInternal = (event: MessageEvent) => {
                if (event?.data) {
                    if (event.data.type === "NOTIFICATION_RECEIVED") {
                        notificationHandler(event as unknown as Y, { source: NotificationSource.SERVICE_WORKER });
                    }
                    if ("isFirebaseMessaging" in event.data) {
                        notificationHandler(event.data as unknown as Y, { source: NotificationSource.FOREGROUND });
                    }
                } else {
                    console.error("Invalid message type:", event);
                    captureMessage("Invalid message type:", {
                        level: "error",
                        extra: {
                            event,
                        },
                    });
                }
            };

            navigator.serviceWorker.addEventListener("message", handleServiceWorkerMessageInternal);

            return () => {
                // unsubscribe();
                navigator.serviceWorker.removeEventListener("message", handleServiceWorkerMessageInternal);
            };
        }, [auth, messaging, notificationHandler]);

        // Memoize the context value to prevent unnecessary re-renders
        const contextValue = useMemo<NotificationContextType>(
            () => ({
                ...notificationStates,
                handleNotification: notificationHandler,
            }),
            [notificationHandler]
        );

        return <NotificationContext.Provider value={contextValue}>{children}</NotificationContext.Provider>;
    };

    const useNotification = () => {
        const ctx = useContext(NotificationContext);
        if (!ctx) throw new Error("useNotification must be used within a NotificationProvider");
        return ctx;
    };

    return {
        Provider: NotificationProvider,
        useNotification,
    };
};
